import React, { useState } from 'react';
import { X, Check, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { markRebrandsReviewed } from '../api';

interface StaleChild {
  id: string;
  brand: string;
  model: string;
}

interface Props {
  baseId: string;
  baseBrand: string;
  baseModel: string;
  staleChildren: StaleChild[];
  onClose: () => void;
  /** Invocato dopo la conferma riuscita, per aggiornare i conteggi/badge in tabella. */
  onReviewed?: () => void;
}

/**
 * Modale di conferma "Segna come aggiornati": elenca i rebrand di una
 * macchina base attualmente "da aggiornare" (ultima associazione della base
 * più recente di quella del figlio) e, alla conferma, azzera la
 * segnalazione per tutti quelli elencati.
 */
export default function MarkRebrandsReviewedModal({
  baseId, baseBrand, baseModel, staleChildren, onClose, onReviewed,
}: Props) {
  const [confirming, setConfirming] = useState(false);

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await markRebrandsReviewed(baseId);
      toast.success(`${res.reviewed} rebrand segnati come aggiornati`);
      onReviewed?.();
      onClose();
    } catch (e: any) {
      toast.error('Errore: ' + (e?.response?.data?.detail ?? e?.message ?? 'unknown'));
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="mark-reviewed-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex-none px-5 py-3.5 border-b border-[#e4e4e1] flex items-center gap-3">
          <h3 className="text-[16px] font-semibold text-[#1f1f1d] flex-1">
            Segna rebrand come aggiornati
          </h3>
          <button onClick={onClose} data-testid="mark-reviewed-close" className="text-[#5c5c57] hover:text-[#3a3a36]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          <div className="text-[14px] text-[#3a3a36] mb-3">
            <strong>{baseBrand} {baseModel}</strong> ha ricevuto nuove associazioni non ancora
            riflesse sui seguenti modelli rebrand:
          </div>
          <ul className="space-y-1.5 mb-3" data-testid="mark-reviewed-list">
            {staleChildren.map(c => (
              <li
                key={c.id}
                data-testid={`mark-reviewed-item-${c.id}`}
                className="flex items-center gap-2 text-[14px] font-semibold text-[#1f1f1d] bg-amber-50 border border-amber-200 rounded-[7px] px-3 py-1.5"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600 flex-none" />
                {c.brand} {c.model}
              </li>
            ))}
          </ul>
          <div className="text-[13px] text-[#5c5c57]">
            Confermando, questi rebrand non verranno più segnalati come "da aggiornare" — a meno che
            {' '}{baseBrand} {baseModel} non riceva ulteriori nuove associazioni in futuro.
          </div>
        </div>

        <div className="flex-none px-5 py-3 border-t border-[#e4e4e1] bg-[#fafafa] flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="mark-reviewed-cancel"
            className="px-3.5 py-2 rounded-[7px] text-[13px] font-semibold text-[#5c5c57] hover:bg-[#f0f0ed] transition-colors"
          >
            Annulla
          </button>
          <button
            onClick={handleConfirm}
            disabled={confirming}
            data-testid="mark-reviewed-confirm"
            className="px-4 py-2 rounded-[7px] bg-[#2b3a8f] text-white text-[13px] font-semibold hover:bg-[#243279] disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            {confirming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
            Segna come aggiornati
          </button>
        </div>
      </div>
    </div>
  );
}
